import { HubEvent } from '../models/hub/hub.event';
import { VisitorDataCache } from '../stores/visitor.calendar.store';
import { MatDialogRef } from '@angular/material/dialog';
import { Location } from '@angular/common';
import { HubPlace } from '../models/hub/hub.place';
import { GoogleAnalyticsService } from './google.analytics.service';

export class SeoService {
  static defaultLogo = 'https://imgoingcalendar.com/assets/imgs/ImGoingLogo-2018-3.png';

  public static createAndInjectGoogleStructureDataScriptForEvent(event: HubEvent) {
    this.removeExistingScript();
    if (!event.isOnlineEvent) {
      const data = this.getStructuredDataForEvent(event);
      this.injectGoogleStructureDataScript(data);
    }
  }

  public static createAndInjectGoogleStructureDataScriptForPlace(place: HubPlace) {
    this.removeExistingScript();
    const data = this.getStructuredDataForPlace(place);
    this.injectGoogleStructureDataScript(data);
  }

  public static getIfNeedToAddNameIdToUrl() {
    let wrapper = document.getElementById('imgoingcalendar-wrapper');
    if (wrapper) {
      let nameId = wrapper.attributes['url-change'] || wrapper.attributes['data-url-change'];
      console.log(nameId);
      return !!nameId;
    }
    return false;
  }

  public static addNameIdToUrlForNonScriptSolution(location: Location, item: HubEvent, dialogRef: MatDialogRef<any>) {
    let fromUrl =
      window.location.href.indexOf('/event-details/') === -1
        ? window.location.href.split(`/${item.hubName}/`)[1]
        : null;
    // change location href, so that there can be a specific url for this place details
    location.go(`${item.hubName}/event-details/${item._id}`);
    dialogRef.beforeClose().subscribe(() => {
      if (fromUrl) {
        location.go(`${item.hubName}/${fromUrl}`);
      } else {
        location.go(`${item.hubName}/events`);
      }
    });

    GoogleAnalyticsService.trackPageView();
  }
  public static addNameIdToUrlForPlaceForNonScriptSolution(
    location: Location,
    place: HubPlace,
    dialogRef: MatDialogRef<any>
  ) {
    let fromUrl =
      window.location.href.indexOf('/place-details/') === -1
        ? window.location.href.split(`/${place.hubName}/`)[1]
        : null;
    // change location href, so that there can be a specific url for this place details
    location.go(`${place.hubName}/place-details/${place._id}`);
    dialogRef.beforeClose().subscribe(() => {
      if (fromUrl) {
        location.go(`${place.hubName}/${fromUrl}`);
      } else {
        location.go(`${place.hubName}/places/all`);
      }
    });

    GoogleAnalyticsService.trackPageView();
  }

  public static addNameIdToUrlForScriptSolution(item: HubEvent, dialogRef?: MatDialogRef<any>) {
    if (window.location.href.indexOf('imgoing-event=') < 0) {
      const oriHref = window.location.href;
      let search = window.location.search ? `&imgoing-event=${item.nameId}` : `?imgoing-event=${item.nameId}`;
      console.log(`oriHref: ${oriHref}, search: ${search}`);
      window.history.pushState(null, null, window.location.href + search);

      dialogRef &&
        dialogRef.beforeClose().subscribe(() => {
          window.history.pushState(null, null, oriHref);
        });
    } else {
      dialogRef &&
        dialogRef.beforeClose().subscribe(() => {
          let oriHref = window.location.href.split('imgoing-event=')[0];
          oriHref = oriHref.endsWith('&') || oriHref.endsWith('?') ? oriHref.substr(0, oriHref.length - 1) : oriHref;
          window.history.pushState(null, null, oriHref);
        });
    }

    GoogleAnalyticsService.trackPageView();
  }
  public static addNameIdToUrlForPlaceForScriptSolution(place: HubPlace, dialogRef?: MatDialogRef<any>) {
    let oriHref = window.location.href.split('imgoing-place=')[0];
    oriHref = oriHref.endsWith('&') || oriHref.endsWith('?') ? oriHref.substr(0, oriHref.length - 1) : oriHref;
    window.history.pushState(null, null, oriHref);

    let search = window.location.search ? `&imgoing-place=${place.nameId}` : `?imgoing-place=${place.nameId}`;
    console.log(`oriHref: ${oriHref}, search: ${search}`);
    window.history.pushState(null, null, oriHref + search);

    dialogRef &&
      dialogRef.beforeClose().subscribe(() => {
        window.history.pushState(null, null, oriHref);
      });

    GoogleAnalyticsService.trackPageView();
  }

  private static removeExistingScript() {
    const deleteJs = document.getElementById('google-search-structured-data');
    deleteJs && deleteJs.parentNode.removeChild(deleteJs);
  }

  private static injectGoogleStructureDataScript(data: {}) {
    const script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('id', 'google-search-structured-data');
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  }

  public static createAndInjectSampleGoogleStructureDataForPlaceScript(place: HubPlace) {
    this.removeExistingScript();
    const data = this.getSampleStructuredDataForPlace(place);
    this.injectGoogleStructureDataScript(data);
  }

  private static getStructuredDataForPlace(place: HubPlace) {
    let type = 'LocalBusiness',
      typeJudged = false;
    if (VisitorDataCache && VisitorDataCache.placeCategories) {
      if (VisitorDataCache.placeCategories['Accommodations']) {
        if (
          VisitorDataCache.placeCategories['Accommodations'].any((x) =>
            place.categoryList.any((y) => x.toLowerCase() === y.toLowerCase())
          )
        ) {
          type = 'LodgingBusiness';
          typeJudged = true;
        }
      }
      if (!typeJudged && VisitorDataCache.placeCategories['Dining & Nightlife']) {
        if (
          VisitorDataCache.placeCategories['Dining & Nightlife'].any((x) =>
            place.categoryList.any((y) => x.toLowerCase() === y.toLowerCase())
          )
        ) {
          type = 'FoodEstablishment';
          typeJudged = true;
        }
      }
      if (!typeJudged && VisitorDataCache.placeCategories['Shopping']) {
        if (
          VisitorDataCache.placeCategories['Shopping'].any((x) =>
            place.categoryList.any((y) => x.toLowerCase() === y.toLowerCase())
          )
        ) {
          type = 'Store';
          typeJudged = true;
        }
      }
    }
    return {
      '@context': 'https://schema.org',
      '@type': type,
      image: [`${place.cover ? place.cover.source || this.defaultLogo : this.defaultLogo}`],
      '@id': `${place._id}`,
      name: `${place.name}`,
      address: {
        '@type': 'PostalAddress',
        streetAddress: `${place.address.address || place.address}`,
        // "addressLocality": "San Jose",
        // "addressRegion": "CA",
        // "postalCode": "95129",
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: place.address.lat,
        longitude: place.address.lng,
      },
      url: `${place.website}`,
      priceRange: `${place.priceRange}`,
      telephone: `${place.phone}`,
    };
  }

  private static getStructuredDataForEvent(event: HubEvent) {
    return {
      '@context': 'https://schema.org',
      '@type': 'Event',
      image: [`${event.cover ? event.cover.source || this.defaultLogo : this.defaultLogo}`],
      '@id': `${event._id}`,
      name: `${event.name || event.title}`,
      startDate: `${event.startTime}`,
      endDate: `${event.endTime}`,
      eventAttendanceMode: `https://schema.org/${
        event.isOnlineEvent ? 'OnlineEventAttendanceMode' : 'OfflineEventAttendanceMode'
      }`,
      eventStatus: `https://schema.org/${event.isOnlineEvent ? 'EventMovedOnline' : 'EventScheduled'}`,
      location: {
        '@type': `${event.isOnlineEvent ? 'VirtualLocation' : 'Place'}`,
        // "name": "Snickerpark Stadium",
        address: {
          '@type': 'PostalAddress',
          streetAddress: `${event.address.address || event.address}`,
          // "addressLocality": "Snickertown",
          // "postalCode": "19019",
          // "addressRegion": "PA",
          addressCountry: 'US',
        },
        url: `${event.eventLink}`,
      },
      description: event.description,
      organizer: {
        '@type': 'Organization',
        name: event.owner.name,
        url: event.hostLink,
      },
    };
  }

  private static getSampleStructuredDataForPlace(place: HubPlace): {} {
    return {
      '@context': 'https://schema.org',
      '@type': 'Store',
      image: [
        'https://example.com/photos/1x1/photo.jpg',
        'https://example.com/photos/4x3/photo.jpg',
        'https://example.com/photos/16x9/photo.jpg',
      ],
      '@id': 'http://davesdeptstore.example.com',
      name: "Dave's Department Store",
      address: {
        '@type': 'PostalAddress',
        streetAddress: '1600 Saratoga Ave',
        addressLocality: 'San Jose',
        addressRegion: 'CA',
        postalCode: '95129',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 37.293058,
        longitude: -121.988331,
      },
      url: 'http://www.example.com/store-locator/sl/San-Jose-Westgate-Store/1427',
      priceRange: '$$$',
      telephone: '+14088717984',
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          opens: '08:00',
          closes: '23:59',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: 'Sunday',
          opens: '08:00',
          closes: '23:00',
        },
      ],
      department: [
        {
          '@type': 'Pharmacy',
          image: [
            'https://example.com/photos/1x1/photo.jpg',
            'https://example.com/photos/4x3/photo.jpg',
            'https://example.com/photos/16x9/photo.jpg',
          ],
          name: "Dave's Pharmacy",
          telephone: '+14088719385',
          openingHoursSpecification: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
              opens: '09:00',
              closes: '19:00',
            },
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: 'Saturday',
              opens: '09:00',
              closes: '17:00',
            },
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: 'Sunday',
              opens: '11:00',
              closes: '17:00',
            },
          ],
        },
      ],
    };
  }
}



// WEBPACK FOOTER //
// D:/Work/Development/ImGoing/IMGoing/web/src/services/seo.service.ts